<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useAuthStore } from '@yuksalish/auth'
import { SmartPickerModal, type SmartPickerOption } from '@yuksalish/ui'
import ReferenceItemCreateModal from './ReferenceItemCreateModal.vue'
import type { ColorReference, ReferenceItem } from '../types'

const props = defineProps<{
  open: boolean
  title: string
  colors: ColorReference[]
}>()

const emit = defineEmits<{
  close: []
  select: [color: ColorReference]
}>()

const auth = useAuthStore()
const search = ref('')
const page = ref(1)
const createOpen = ref(false)
const pageSize = 8
const canCreate = computed(() => auth.user?.permissions.includes('products.update') ?? false)
const filteredColors = computed(() => {
  const query = search.value.trim().toLocaleLowerCase()
  if (!query) return props.colors
  return props.colors.filter((color) =>
    [color.name, color.code]
      .some((value) => String(value ?? '').toLocaleLowerCase().includes(query)),
  )
})
const totalPages = computed(() => Math.max(1, Math.ceil(filteredColors.value.length / pageSize)))
const visibleColors = computed(() => {
  const start = (page.value - 1) * pageSize
  return filteredColors.value.slice(start, start + pageSize)
})
const visibleOptions = computed<SmartPickerOption[]>(() =>
  visibleColors.value.map((color) => ({
    id: color.id,
    title: color.name,
    subtitle: color.code || 'No code',
    meta: `#${color.id}`,
  })),
)

watch(() => props.open, (open) => {
  if (open) {
    search.value = ''
    page.value = 1
  }
}, { immediate: true })

watch(search, () => {
  if (!props.open) return
  page.value = 1
})

function selectOption(option: SmartPickerOption): void {
  const color = visibleColors.value.find((entry) => entry.id === Number(option.id))
  if (color) emit('select', color)
}

function handleCreated(color: ReferenceItem): void {
  createOpen.value = false
  emit('select', color as ColorReference)
}
</script>

<template>
  <SmartPickerModal
    :open="open"
    :title="title"
    eyebrow="Color repository"
    search-label="Search colors"
    search-placeholder="Name or code"
    :search-value="search"
    :loading="false"
    error-message=""
    empty-message="No matching colors found."
    :items="visibleOptions"
    :page="page"
    :total-pages="totalPages"
    :page-label="`${filteredColors.length} items · Page ${page} of ${totalPages}`"
    close-aria-label="Close color picker"
    retry-label="Retry"
    previous-label="Previous"
    next-label="Next"
    cancel-label="Cancel"
    @close="emit('close')"
    @previous="page--"
    @next="page++"
    @select="selectOption"
    @update:search-value="search = $event"
  >
    <template #helper>
      <div class="picker-helper">
        <div>
          <strong>Need a new color?</strong>
          <p>Create it here, then it will be selected automatically.</p>
        </div>
        <button type="button" :disabled="!canCreate" @click="createOpen = true">
          Add new
        </button>
      </div>
    </template>
  </SmartPickerModal>
  <ReferenceItemCreateModal
    :open="createOpen"
    title="Create color"
    type="color"
    @close="createOpen = false"
    @created="handleCreated"
  />
</template>

<style scoped>
.picker-helper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #d7dfdc;
  background: #f7fbff;
}

.picker-helper strong {
  color: #173454;
  font-size: 13px;
}

.picker-helper p {
  margin: 0;
  color: #66726e;
  font-size: 12px;
  line-height: 1.4;
}

.picker-helper button {
  padding: 8px 12px;
  border: 1px solid #b9c5c1;
  color: #285b9a;
  background: #fff;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.picker-helper button:disabled {
  color: #9aa7a3;
  background: #f3f6f5;
  cursor: default;
}
</style>
